import { useNavigate } from "react-router-dom";

import { SidebarItem, ICON_PATHS } from "@/components/layout/SidebarItem";
import type { AuthUser } from "@/types";
import { canSeeTab, hasPermission } from "@/types";

interface AppSidebarProps {
  authUser: AuthUser;
  open: boolean;
  effectivePage: string;
  newCandidateCount: number;
  onClearCandidateBadge: () => void;
  onClose: () => void;
}

export function AppSidebar({ authUser, open, effectivePage, newCandidateCount, onClearCandidateBadge, onClose }: AppSidebarProps) {
  const navigate = useNavigate();

  const go = (path: string) => {
    navigate(path);
    if (window.innerWidth < 768) onClose();
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-20 bg-black/30 md:hidden" onClick={onClose} />
      )}
      <aside className={[
        "flex flex-col border-r border-surface-secondary bg-white transition-all duration-200",
        "fixed inset-y-14 left-0 z-30 md:relative md:inset-auto md:z-auto",
        open ? "w-56" : "-translate-x-full md:translate-x-0 md:w-16",
      ].join(" ")}>
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
          {canSeeTab(authUser, "candidates") && (
            <SidebarItem
              iconPath={ICON_PATHS.candidates}
              label="Candidates"
              active={effectivePage === "candidates"}
              collapsed={!open}
              onClick={() => { go("/candidates"); onClearCandidateBadge(); }}
              badge={newCandidateCount > 0 && effectivePage !== "candidates" ? newCandidateCount : undefined}
            />
          )}
          {hasPermission(authUser, "candidates:read") && (
            <SidebarItem iconPath={ICON_PATHS.pipeline} label="Pipeline" active={effectivePage === "pipeline"} collapsed={!open} onClick={() => go("/pipeline")} />
          )}
          {canSeeTab(authUser, "calendar") && (
            <SidebarItem iconPath={ICON_PATHS.calendar} label="Calendar" active={effectivePage === "calendar"} collapsed={!open} onClick={() => go("/calendar")} />
          )}
          {canSeeTab(authUser, "positions") && (
            <SidebarItem iconPath={ICON_PATHS.positions} label="Positions" active={effectivePage === "positions"} collapsed={!open} onClick={() => go("/positions")} />
          )}
          {canSeeTab(authUser, "matrices") && (
            <SidebarItem iconPath={ICON_PATHS.matrices} label="Matrix" active={effectivePage === "matrices"} collapsed={!open} onClick={() => go("/matrices")} />
          )}
          {canSeeTab(authUser, "archived") && (
            <SidebarItem iconPath={ICON_PATHS.archived} label="Archived" active={effectivePage === "archived"} collapsed={!open} onClick={() => go("/archived")} />
          )}
          {canSeeTab(authUser, "organizations") && (
            <SidebarItem
              iconPath={ICON_PATHS.organizations}
              label="Organizations"
              active={effectivePage === "organizations" || effectivePage === "org-analytics"}
              collapsed={!open}
              onClick={() => go("/organizations")}
            />
          )}
          {canSeeTab(authUser, "users") && (
            <SidebarItem iconPath={ICON_PATHS.users} label="Users" active={effectivePage === "users"} collapsed={!open} onClick={() => go("/users")} />
          )}
          {(canSeeTab(authUser, "roles") || (authUser.role === "admin" && !!authUser.org_id)) && (
            <SidebarItem iconPath={ICON_PATHS.roles} label="Roles" active={effectivePage === "roles"} collapsed={!open} onClick={() => go("/roles")} />
          )}
          {hasPermission(authUser, "roles:manage") && (
            <SidebarItem iconPath={ICON_PATHS.permissions} label="Permissions" active={effectivePage === "permissions"} collapsed={!open} onClick={() => go("/permissions")} />
          )}
        </nav>

        {open && (
          <div className="border-t border-surface-secondary px-2 py-3">
            <p className="text-center text-[10px] font-medium text-text-secondary">
              Powered by <span className="font-bold text-emerald-600">DitoSoft</span>
            </p>
          </div>
        )}
      </aside>
    </>
  );
}
